import styled from "styled-components";
import { Box, Flex } from "rebass";
import { BiChevronDown } from "react-icons/bi";
import { useRecoilState, useSetRecoilState } from "recoil";
import { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { ESupportedGames } from "MVVM/@types";
import { GAME_TO_ICON_MAP } from "MVVM/Components/GameSelector";
import { MatchHistoryGame } from "MVVM/State/matchHistory";
import { UserGamePreference } from "MVVM/State/user";
import { useLinkedAccounts } from "MVVM/Hooks/useLinkedAccounts";

const GAME_LABELS: Partial<Record<ESupportedGames, string>> = {
  [ESupportedGames.CS2]: "CS2",
  [ESupportedGames.LOL]: "LoL",
  [ESupportedGames.FORTNITE]: "Fortnite",
  [ESupportedGames.DOTA2]: "Dota 2",
  [ESupportedGames.VALORANT]: "Valorant",
};

const GameSelector = () => {
  const [matchHistoryGame, setMatchHistoryGame] =
    useRecoilState(MatchHistoryGame);
  const { hasSteamLinked, hasRiotLinked, hasEpicLinked } = useLinkedAccounts();
  const setUserGamePreference = useSetRecoilState(UserGamePreference);
  const history = useHistory();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasGameParam = params.get("game");

    if (matchHistoryGame === ESupportedGames.NONE && !hasGameParam) {
      if (hasSteamLinked) {
        setMatchHistoryGame(ESupportedGames.CS2);
      } else if (hasRiotLinked) {
        setMatchHistoryGame(ESupportedGames.LOL);
      } else if (hasEpicLinked) {
        setMatchHistoryGame(ESupportedGames.FORTNITE);
      }
    }
  }, [
    matchHistoryGame,
    hasRiotLinked,
    hasEpicLinked,
    hasSteamLinked,
    setMatchHistoryGame,
  ]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const onGameChange = (game: ESupportedGames) => {
    setMatchHistoryGame(game);
    setUserGamePreference(game);
    setOpen(false);

    const params = new URLSearchParams(location.search);
    params.delete("game");
    params.delete("matchId");

    const newSearch = params.toString();
    const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ""}`;

    if (newUrl !== `${location.pathname}${location.search}`) {
      history.replace(newUrl);
    }
  };

  const GAME_OPTIONS = [
    ESupportedGames.CS2,
    ESupportedGames.LOL,
    ESupportedGames.FORTNITE,
    ESupportedGames.DOTA2,
  ];

  return (
    <GameFilterContainer ref={containerRef}>
      <GameTriggerButton onClick={() => setOpen((v) => !v)}>
        {GAME_TO_ICON_MAP[matchHistoryGame]}
        <span>{GAME_LABELS[matchHistoryGame] ?? "Game"}</span>
        <BiChevronDown
          size={14}
          style={{
            marginLeft: 2,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 0.15s",
          }}
        />
      </GameTriggerButton>

      {open && (
        <GamePanel>
          {GAME_OPTIONS.map((game) => (
            <GameOption
              key={game}
              $active={matchHistoryGame === game}
              onClick={() => onGameChange(game)}
            >
              {GAME_TO_ICON_MAP[game]}
              <span>{GAME_LABELS[game]}</span>
            </GameOption>
          ))}
        </GamePanel>
      )}
    </GameFilterContainer>
  );
};

const GameFilterContainer = styled(Box)`
  position: relative;
  display: inline-block;
`;

const GameTriggerButton = styled.button`
  align-items: center;
  background: ${({ theme }) => theme.colors.navy};
  border: none;
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.ice};
  cursor: pointer;
  display: inline-flex;
  font-family: Barlow;
  font-size: 15px;
  font-weight: 500;
  gap: 6px;
  justify-content: center;
  padding: 10px 16px;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors.navy50};
    opacity: 0.9;
  }
`;

const GamePanel = styled(Flex)`
  background: ${({ theme }) => theme.colors.navy200};
  border: 1px solid ${({ theme }) => theme.colors.navy50};
  border-radius: 10px;
  box-shadow: 0px 8px 24px 0px rgba(0, 0, 0, 0.4);
  flex-direction: column;
  min-width: 160px;
  padding: 6px;
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  z-index: 1000;
`;

const GameOption = styled.button<{ $active: boolean }>`
  align-items: center;
  background: ${({ theme, $active }) =>
    $active ? theme.colors.midnightAlpha50 : "transparent"};
  border: none;
  border-radius: 8px;
  color: ${({ theme, $active }) =>
    $active ? theme.colors.envy : theme.colors.ice};
  cursor: pointer;
  display: flex;
  font-family: Barlow;
  font-size: 14px;
  font-weight: 500;
  gap: 10px;
  padding: 10px 12px;
  text-align: left;
  transition: background 0.15s;
  width: 100%;

  &:hover {
    background: ${({ theme }) => theme.colors.midnightAlpha50};
    color: ${({ theme }) => theme.colors.envy};
  }
`;

export { GameSelector };
